import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/ui/loader";
import tripService from "@/services/tripService";
import { AlertTriangle, FileText } from "lucide-react";
import { formatTripRoute } from "@/lib/tripRouteUtils";
import { getRakePointNameMap } from "@/lib/rakePointNameCache";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";

const ViewTripDetailsContent = ({ tripDetails, loading }) => {
  const { toast } = useToast();
  const [pendingRequest, setPendingRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [rakePointNameById, setRakePointNameById] = useState({});
  // const [ddNumbers, setDdNumbers] = useState({});
  // const [dispatchedItems, setDispatchedItems] = useState({});

  // const handleDdNumberChange = (key, value) => {
  //   setDdNumbers((prev) => ({ ...prev, [key]: value }));
  // };

  // const handleDdSubmit = (item, index) => {
  //   const key = item.id || index;
  //   const ddVal = ddNumbers[key]?.trim();
  //   if (!ddVal) return;

  //   setDispatchedItems((prev) => ({ ...prev, [key]: ddVal }));
  //   toast({
  //     title: "Success",
  //     description: `DD Number ${ddVal} submitted successfully`,
  //   });
  // };

  useEffect(() => {
    getRakePointNameMap().then(setRakePointNameById);
  }, []);

  useEffect(() => {
    if (tripDetails?.id && tripDetails?.status !== 'PENDING') {
      fetchPendingRequest();
    } else {
      setPendingRequest(null);
    }
  }, [tripDetails]);

  const fetchPendingRequest = async () => {
    try {
      setLoadingRequest(true);
      const res = await tripService.getEditRequests('PENDING');
      const data = res?.data || res || [];
      const ticket = data.find(req => req.trip_id === tripDetails.id);
      setPendingRequest(ticket);
    } catch (err) {
      console.error('Failed to fetch pending edit requests:', err);
    } finally {
      setLoadingRequest(false);
    }
  };

  if (loading || loadingRequest) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader size="lg" message="Loading trip details..." />
      </div>
    );
  }

  if (!tripDetails) return null;

  const movementScope = tripDetails.movement || 'Primary';
  const movementMode = (tripDetails.movementType || tripDetails.movement_type || 'ROAD').toUpperCase();

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-green-600';
      case 'COMPLETED':
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'text-blue-600';
      case 'DELAYED':
        return 'text-red-600';
      case 'SCHEDULED':
        return 'text-yellow-600';
      case 'PENDING':
        return 'text-orange-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'COMPLETED':
        return 'Completed';
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'Auto Completed';
      case 'DELAYED':
        return 'Delayed';
      case 'SCHEDULED':
        return 'Scheduled';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatUserName = (user) => {
    if (!user) return 'N/A';
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return fullName || user.username || 'N/A';
  };

  const CustomDashboardCard = ({ name, count, unit }) => {
    if (count === null || count === undefined || count === "") return null;
    return (
      <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
        style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
        <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">
              {name}
            </div>
            <div className="text-xs font-normal break-all">
              {count} {unit}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {pendingRequest && (
        <div className="p-4 rounded-[16px] border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 space-y-3">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 font-bold text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600" /> Correction Request Pending Approval
          </div>
          <div className="text-xs text-amber-700 dark:text-amber-400 space-y-2">
            <p>A request to update this trip was submitted on <strong>{new Date(pendingRequest.created_at).toLocaleString()}</strong> by <strong>{pendingRequest.requester_username}</strong>. Reason: <em>"{pendingRequest.request_reason}"</em></p>
            <div className="bg-amber-100/50 dark:bg-amber-950/40 p-2.5 rounded-lg space-y-1">
              <span className="font-semibold block mb-1">Requested Changes:</span>
              {pendingRequest.changes?.map((ch, idx) => (
                <div key={ch.id || idx} className="flex gap-2">
                  <span className="font-medium">{ch.display_label || ch.field_name}:</span>
                  <span className="line-through text-muted-foreground">{ch.old_value || 'N/A'}</span>
                  <span>➔</span>
                  <span className="font-bold">{ch.new_value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main General Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CustomDashboardCard
          name="Trip Number"
          count={tripDetails.trip_number}
          unit=""
        />

        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
          style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">
                Status
              </div>
              <div className={`text-xs font-normal ${getStatusColor(tripDetails.status)}`}>
                {formatStatus(tripDetails.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CustomDashboardCard
          name="Trip Scope"
          count={movementScope}
          unit=""
        />

        <CustomDashboardCard
          name="Movement Mode"
          count={movementMode}
          unit=""
        />

        <CustomDashboardCard
          name="Route"
          count={formatTripRoute(tripDetails, rakePointNameById)}
          unit=""
        />

        {tripDetails.source_rake_movement_id && (
          <CustomDashboardCard
            name="Trip Flow"
            count="Rake Received → Secondary Dispatch"
            unit=""
          />
        )}

        <CustomDashboardCard
          name="Created At"
          count={formatDate(tripDetails.created_at)}
          unit=""
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <CustomDashboardCard
          name="Scheduled Start Time"
          count={formatDate(tripDetails.scheduled_start_time)}
          unit=""
        />

        <CustomDashboardCard
          name="Scheduled End Time"
          count={formatDate(tripDetails.scheduled_end_time)}
          unit=""
        />
      </div>

      {/* Port Information Card */}
      {tripDetails.port && (
        <Card className="border border-border rounded-[16px] bg-card w-full">
          <CardContent className="p-0">
            <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
              <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                <h3 className="text-xs font-bold text-foreground mb-3">Port Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDashboardCard name="Port Name" count={tripDetails.port.port_name} unit="" />
                  <CustomDashboardCard name="Port Code" count={tripDetails.port.port_code ? String(tripDetails.port.port_code).toUpperCase() : null} unit="" />
                  <CustomDashboardCard name="Port Type" count={tripDetails.port.porttype} unit="" />
                  <CustomDashboardCard name="Coordinates" count={tripDetails.port.latitude && tripDetails.port.longitude ? `${tripDetails.port.latitude}, ${tripDetails.port.longitude}` : null} unit="" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Route Information Card */}
      {(tripDetails.start_address || tripDetails.start_district || tripDetails.start_state || (tripDetails.start_latitude && tripDetails.start_longitude) || (tripDetails.end_latitude && tripDetails.end_longitude)) && (
        <Card className="border border-border rounded-[16px] bg-card w-full">
          <CardContent className="p-0">
            <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
              <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                <h3 className="text-xs font-bold text-foreground mb-3">Route Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDashboardCard
                    name="Start Location Address"
                    count={tripDetails.start_address || (tripDetails.start_district || tripDetails.start_state ? `${tripDetails.start_district || ""}, ${tripDetails.start_state || ""}` : null)}
                    unit=""
                  />
                  <CustomDashboardCard
                    name="Start Coordinates"
                    count={tripDetails.start_latitude && tripDetails.start_longitude ? `${tripDetails.start_latitude}, ${tripDetails.start_longitude}` : null}
                    unit=""
                  />
                  <CustomDashboardCard
                    name="End Location Address"
                    count={tripDetails.end_address || (tripDetails.end_district || tripDetails.end_state ? `${tripDetails.end_district || ""}, ${tripDetails.end_state || ""}` : null)}
                    unit=""
                  />
                  <CustomDashboardCard
                    name="End Coordinates"
                    count={tripDetails.end_latitude && tripDetails.end_longitude ? `${tripDetails.end_latitude}, ${tripDetails.end_longitude}` : null}
                    unit=""
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle and Driver details */}
      {(tripDetails.driver || tripDetails.vehicle) && (
        <>
          <Card className="border border-border rounded-[16px] bg-card w-full">
            <CardContent className="p-0">
              <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
                style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
                <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                  <h3 className="text-xs font-bold text-foreground mb-3">Vehicle Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CustomDashboardCard
                      name="Vehicle Number"
                      count={tripDetails.vehicle?.vehicle_number}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Vehicle Type"
                      count={tripDetails.vehicle?.vehicle_type}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Capacity"
                      count={tripDetails.vehicle?.capacity}
                      unit={tripDetails.vehicle?.capacity ? 'kg' : ''}
                    />
                    <CustomDashboardCard
                      name="Vehicle Status"
                      count={tripDetails.vehicle ? (tripDetails.vehicle.is_active ? 'Active' : 'Inactive') : null}
                      unit=""
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border rounded-[16px] bg-card w-full">
            <CardContent className="p-0">
              <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
                style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
                <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                  <h3 className="text-xs font-bold text-foreground mb-3">Driver Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CustomDashboardCard
                      name="Driver Name"
                      count={formatUserName(tripDetails.driver?.user)}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Username"
                      count={tripDetails.driver?.user?.username}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Phone"
                      count={tripDetails.driver?.user?.phone_number}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Email"
                      count={tripDetails.driver?.user?.email}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="License Number"
                      count={tripDetails.driver?.license_number}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="License Expiry"
                      count={formatDate(tripDetails.driver?.license_expiry)}
                      unit=""
                    />
                    <CustomDashboardCard
                      name="Driver Status"
                      count={tripDetails.driver ? (tripDetails.driver.is_available ? 'Available' : 'Not Available') : null}
                      unit=""
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Product Information Card */}
      <Card className="border border-border rounded-[16px] bg-card w-full">
        <CardContent className="p-0">
          <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
            style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
            <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
              <h3 className="text-xs font-bold text-foreground mb-3">Product / Cargo Information</h3>

              {tripDetails.items && tripDetails.items.length > 0 ? (
                tripDetails.items.map((item, index) => {
                  const isRetailer = !!item.retailer || !!item.retailer_id;
                  const client = isRetailer ? item.retailer : item.distributor;
                  const clientLabel = isRetailer ? "Retailer" : "Distributor";

                  return (
                    <div key={item.id || index} className="mb-6 border-b border-border/50 pb-4 last:border-b-0 last:pb-0">
                      <h4 className="text-xs font-semibold text-foreground mb-2">Item {index + 1}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <CustomDashboardCard
                          name="Product Type"
                          count={item.product_type?.name}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Quantity"
                          count={item.product_quantity}
                          unit={item.product_type?.unit_of_measure || ''}
                        />

                        <CustomDashboardCard
                          name="Invoice Number"
                          count={item.invoice_number}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="DC Number"
                          count={item.dc_number}
                          unit=""
                        />

                        {/* SAP Dispatch / DD Number — COMMENTED OUT */}
                        {/* {(() => {
                          const isDispatched = item.has_sap_dispatch || !!dispatchedItems[item.id || index];
                          const currentDd = dispatchedItems[item.id || index] || item.dd_number;

                          if (!isDispatched) {
                            return (
                              <div
                                className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
                                style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}
                              >
                                <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                                  <div className="space-y-1">
                                    <div className="text-xs font-medium text-muted-foreground">
                                      DD Number
                                    </div>
                                    <div className="flex items-center gap-2 pt-0.5">
                                      <Input
                                        type="text"
                                        placeholder="Enter DD No"
                                        value={ddNumbers[item.id || index] || ''}
                                        onChange={(e) => handleDdNumberChange(item.id || index, e.target.value)}
                                        className="h-7 text-xs px-2 bg-background flex-1"
                                      />
                                      <Button
                                        type="button"
                                        size="sm"
                                        className="h-7 px-3 text-xs"
                                        onClick={() => handleDdSubmit(item, index)}
                                        disabled={!ddNumbers[item.id || index]?.trim()}
                                      >
                                        Submit
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <CustomDashboardCard
                              name="SAP Dispatch"
                              count={currentDd ? `Dispatched (DD: ${currentDd})` : 'Dispatched'}
                              unit=""
                            />
                          );
                        })()} */}

                        {item.eway_bill_number && (
                          <CustomDashboardCard
                            name="E-way Bill Number"
                            count={item.eway_bill_number}
                            unit=""
                          />
                        )}

                        {item.eway_bill_date && (
                          <CustomDashboardCard
                            name="E-way Bill Date"
                            count={formatDate(item.eway_bill_date)}
                            unit=""
                          />
                        )}

                        <CustomDashboardCard
                          name="Estimated Kms"
                          count={item.estimated_kms}
                          unit="KM"
                        />

                        <CustomDashboardCard
                          name={`${clientLabel} Company`}
                          count={client?.company_name || client?.name}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Name"
                          count={item.consignee_name}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Contact Person"
                          count={item.consignee_contact_person}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Phone"
                          count={item.consignee_phone}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Email"
                          count={item.consignee_email}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Address"
                          count={item.consignee_address}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Coordinates"
                          count={item.consignee_latitude && item.consignee_longitude ? `${item.consignee_latitude}, ${item.consignee_longitude}` : (item.end_latitude && item.end_longitude ? `${item.end_latitude}, ${item.end_longitude}` : null)}
                          unit=""
                        />

                        <CustomDashboardCard
                          name="Consignee Delivery Instructions"
                          count={item.consignee_delivery_instructions}
                          unit=""
                        />
                      </div>

                      {/* Item Uploaded files */}
                      {(() => {
                        const itemFiles = (item.file_uploads && item.file_uploads.length > 0)
                          ? item.file_uploads
                          : (tripDetails.file_uploads && tripDetails.file_uploads.length > 0 ? tripDetails.file_uploads : []);

                        if (!itemFiles || itemFiles.length === 0) return null;

                        return (
                          <div className="mt-4 pt-3 border-t border-border/40">
                            <span className="text-[11px] font-semibold text-muted-foreground tracking-wider block mb-2">Attached Documents</span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                              {itemFiles.map((file, idx) => {
                                const isInvoice = file.file_type === 'invoice';
                                const typeLabel = isInvoice ? 'Invoice' : (file.file_type === 'eway_bill' ? 'E-way Bill' : 'Document');

                                const getCleanFileName = (fileObj) => {
                                  const raw = fileObj.original_filename || fileObj.file_name || fileObj.name || fileObj.description || fileObj.file_url || fileObj.url;
                                  if (!raw || typeof raw !== 'string') return `${typeLabel} ${idx + 1}`;
                                  try {
                                    const url = new URL(raw);
                                    const name = url.pathname.split('/').filter(Boolean).pop();
                                    if (name) return decodeURIComponent(name);
                                  } catch {
                                    if (raw.includes('/')) {
                                      const name = raw.split('/').filter(Boolean).pop();
                                      if (name) return decodeURIComponent(name);
                                    }
                                  }
                                  return raw;
                                };

                                const fileTitle = getCleanFileName(file);

                                return (
                                  <div
                                    key={file.id || idx}
                                    className="flex items-center gap-2.5 p-2.5 rounded-xl border border-border/80 bg-muted/20 shadow-2xs"
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <div className="w-8 h-8 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-4 h-4" />
                                      </div>
                                      <div className="min-w-0">
                                        <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-primary/10 text-primary inline-block mb-0.5">
                                          {typeLabel}
                                        </span>
                                        <p className="text-xs font-medium text-foreground truncate max-w-[240px]" title={fileTitle}>
                                          {fileTitle}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDashboardCard
                    name="Product Type"
                    count={tripDetails.product_type?.name}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Quantity"
                    count={tripDetails.product_quantity}
                    unit={tripDetails.product_type?.unit_of_measure || ''}
                  />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Distributor Information Card */}
      {(tripDetails.distributor || tripDetails.items?.some(i => i.distributor)) && (
        <Card className="border border-border rounded-[16px] bg-card w-full">
          <CardContent className="p-0">
            <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
              <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                <h3 className="text-xs font-bold text-foreground mb-3">Distributor Information</h3>

                {tripDetails.items && tripDetails.items.length > 0 ? (
                  tripDetails.items.map((item, index) => (
                    item.distributor && (
                      <div key={item.id || index} className="mb-4 last:mb-0">
                        <h4 className="text-xs font-semibold text-foreground mb-2">Item {index + 1} Distributor</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <CustomDashboardCard
                            name="Company Name"
                            count={item.distributor.company_name}
                            unit=""
                          />

                          <CustomDashboardCard
                            name="Contact Person"
                            count={formatUserName(item.distributor.user)}
                            unit=""
                          />

                          <CustomDashboardCard
                            name="Username"
                            count={item.distributor.user?.username}
                            unit=""
                          />

                          <CustomDashboardCard
                            name="Phone"
                            count={item.distributor.user?.phone_number}
                            unit=""
                          />

                          <CustomDashboardCard
                            name="Email"
                            count={item.distributor.user?.email}
                            unit=""
                          />

                          <CustomDashboardCard
                            name="Address"
                            count={item.distributor.address}
                            unit=""
                          />
                        </div>
                      </div>
                    )
                  ))
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <CustomDashboardCard
                      name="Company Name"
                      count={tripDetails.distributor?.company_name}
                      unit=""
                    />

                    <CustomDashboardCard
                      name="Contact Person"
                      count={formatUserName(tripDetails.distributor?.user)}
                      unit=""
                    />

                    <CustomDashboardCard
                      name="Username"
                      count={tripDetails.distributor?.user?.username}
                      unit=""
                    />

                    <CustomDashboardCard
                      name="Phone"
                      count={tripDetails.distributor?.user?.phone_number}
                      unit=""
                    />

                    <CustomDashboardCard
                      name="Email"
                      count={tripDetails.distributor?.user?.email}
                      unit=""
                    />

                    <CustomDashboardCard
                      name="Address"
                      count={tripDetails.distributor?.address}
                      unit=""
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transporter Information Card */}
      {tripDetails.transporter && (
        <Card className="border border-border rounded-[16px] bg-card w-full">
          <CardContent className="p-0">
            <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
              <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                <h3 className="text-xs font-bold text-foreground mb-3">Transporter Information</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDashboardCard
                    name="Company Name"
                    count={tripDetails.transporter.company_name}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Registration Number"
                    count={tripDetails.transporter.registration_number}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Contact Person"
                    count={tripDetails.transporter.contact_person || formatUserName(tripDetails.transporter.user)}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Username"
                    count={tripDetails.transporter.user?.username}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Phone"
                    count={tripDetails.transporter.user?.phone_number}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Email"
                    count={tripDetails.transporter.user?.email}
                    unit=""
                  />

                  <CustomDashboardCard
                    name="Address"
                    count={tripDetails.transporter.address}
                    unit=""
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consignee Information Card (Fallback if items empty) */}
      {!tripDetails.items?.length && (tripDetails.consignee_name || tripDetails.consignee_address) && (
        <Card className="border border-border rounded-[16px] bg-card w-full">
          <CardContent className="p-0">
            <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform"
              style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
              <div className="border border-border px-2 py-2 rounded-[16px] bg-background">
                <h3 className="text-xs font-bold text-foreground mb-3">Consignee Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <CustomDashboardCard name="Consignee Name" count={tripDetails.consignee_name} unit="" />
                  <CustomDashboardCard name="Contact Person" count={tripDetails.consignee_contact_person} unit="" />
                  <CustomDashboardCard name="Phone" count={tripDetails.consignee_phone} unit="" />
                  <CustomDashboardCard name="Email" count={tripDetails.consignee_email} unit="" />
                  <CustomDashboardCard name="Address" count={tripDetails.consignee_address} unit="" />
                  <CustomDashboardCard name="Coordinates" count={tripDetails.consignee_latitude && tripDetails.consignee_longitude ? `${tripDetails.consignee_latitude}, ${tripDetails.consignee_longitude}` : null} unit="" />
                  <CustomDashboardCard name="Delivery Instructions" count={tripDetails.consignee_delivery_instructions} unit="" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ViewTripDetailsContent;
